// Rule-based "I'm Frozen" step generator.
// No LLM. Detects an action category from the user's task and a noun phrase,
// then returns 3–5 tiny, ADHD-friendly restart steps.

const CATEGORIES = [
  {
    key: "write",
    match: /\b(write|draft|outline|compose|author|type|journal|blog|essay|proposal|doc|document|article|memo|note|content|copy|post|newsletter|chapter|book|story|paragraph|summary)\b/i,
    steps: (noun) => [
      `Open the ${noun || "document"} and put your cursor in it.`,
      "Write one rough sentence — it can be terrible.",
      "Add a quick note of what you want to say (bullet points are fine).",
      "Stop after two minutes if you need to. You can come back.",
    ],
  },
  {
    key: "email",
    match: /\b(email|reply|respond|message|dm|text|slack|inbox|chat|follow up|follow-up)\b/i,
    steps: (noun) => [
      noun
        ? `Open the email or thread from ${noun}.`
        : "Open the thread. Don't read the whole chain.",
      "Type a one-line draft: 'Hey — short reply incoming.'",
      "Add the actual answer in plain words. Skip the polish.",
      "Send it before you re-read more than once.",
    ],
  },
  {
    key: "code",
    match: /\b(code|coding|program|programming|debug|fix|bug|build|build a|implement|refactor|review|pr|pull request|deploy|merge|test|feature|component|api|endpoint|function|script)\b/i,
    steps: (noun) => [
      `Open the file or tab for the ${noun || "task"}.`,
      "Add a TODO comment with what you actually want to do, in plain English.",
      "Make one small change — even a comment counts.",
      "Run it once, even if it's still broken.",
      "Pick the smallest next thing and stop there.",
    ],
  },
  {
    key: "read",
    match: /\b(read|reading|review|study|learn|research|skim|paper|book|chapter|article|spec|docs|documentation)\b/i,
    steps: (noun) => [
      `Open the ${noun || "page"}. Don't worry about reading it all.`,
      "Read just the first paragraph or section header.",
      "Highlight one sentence that feels important.",
      "Write a one-line note of what you took away.",
    ],
  },
  {
    key: "call",
    match: /\b(call|phone|meeting|zoom|huddle|interview|stand[- ]?up|sync|1[- ]?on[- ]?1)\b/i,
    steps: (noun) => [
      `Open the calendar invite or notes for the ${noun || "call"}.`,
      "Write down one thing you want to say or ask.",
      "Drink some water. Sit somewhere comfortable.",
      "Join two minutes early so you don't have to rush.",
    ],
  },
  {
    key: "decide",
    match: /\b(decide|decision|choose|pick|figure out|figure-out|plan|planning)\b/i,
    steps: (noun) => [
      `Write the ${noun || "decision"} at the top of a page.`,
      "List the two most likely options. Two only.",
      "Write one sentence under each: what's good, what's hard.",
      "Pick one for the next 24 hours. You can change later.",
    ],
  },
  {
    key: "clean",
    match: /\b(clean|tidy|organi[sz]e|sort|file|inbox zero|laundry|dishes|room|desk|fold)\b/i,
    steps: (noun) => [
      "Set a 5-minute timer. Just five.",
      `Pick up or sort one thing in the ${noun || "space"}.`,
      "Do another one if you feel like it. No pressure.",
      "Stop when the timer ends, even if you're mid-task.",
    ],
  },
  {
    key: "exercise",
    match: /\b(exercise|workout|gym|run|walk|stretch|yoga|move)\b/i,
    steps: () => [
      "Put on the clothes. That's the whole step.",
      "Stand up and stretch for 30 seconds.",
      "Step outside or onto the mat.",
      "Move for 2 minutes. If you stop there, that still counts.",
    ],
  },
  {
    key: "submit",
    match: /\b(submit|apply|application|form|tax|pay|invoice|paperwork|expense)\b/i,
    steps: (noun) => [
      `Open the ${noun || "form"}. Don't read the whole thing yet.`,
      "Fill in the easiest field — name, email, or date.",
      "Save a draft if you can. You're allowed to leave and come back.",
      "Fill in one harder field. Then stop if you need to.",
    ],
  },
  {
    key: "start",
    match: /\b(start|begin|kick off|kickoff|launch)\b/i,
    steps: (noun) => [
      `Open the tab or doc for ${noun || "this"}.`,
      "Write the title or first heading. That's it.",
      "Add one bullet under it. Even a placeholder.",
      "Stop. You've started — that was the hard part.",
    ],
  },
];

const STOPWORDS = new Set([
  "a","an","the","my","your","our","their","this","that","these","those",
  "to","for","with","on","in","at","of","and","or","but","up","down",
  "i","im","i'm","need","needs","want","wants","have","has","got","gotta",
  "today","tomorrow","tonight","now","later","just","really","actually",
  "stuck","frozen","be","is","am","are","was","were","do","does","did",
  "going","gonna","try","trying","still","yet","also",
]);

function extractNoun(text, verbKey) {
  const cleaned = text
    .toLowerCase()
    .replace(/[^a-z0-9 \-]/gi, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !STOPWORDS.has(w));

  // Drop the matched verb itself and obvious action words
  const filtered = cleaned.filter((w) => {
    if (verbKey && w.startsWith(verbKey.slice(0, 3))) return false;
    if (
      [
        "write","draft","email","reply","read","call","decide",
        "clean","run","walk","start","begin","submit","apply",
        "code","build","fix","debug","review","study","learn",
      ].includes(w)
    ) return false;
    return true;
  });

  if (!filtered.length) return null;
  // Take last 1-3 meaningful words as the object
  return filtered.slice(-3).join(" ");
}

export function generateFrozenSteps(rawInput) {
  const text = (rawInput || "").trim();
  if (!text) {
    return {
      task: "the thing in front of you",
      category: "default",
      steps: [
        "Notice you're stuck. That's already a real step.",
        "Open the tab or app you'd need.",
        "Set a 2-minute timer and do anything tiny.",
        "Stop without guilt when the timer ends.",
      ],
    };
  }

  const found = CATEGORIES.find((c) => c.match.test(text));
  const category = found?.key || "default";
  const noun = extractNoun(text, category);

  const steps = found
    ? found.steps(noun)
    : [
        `Open whatever you'd need for "${text}".`,
        "Set a 2-minute timer.",
        "Take one tiny visible action — anything counts.",
        "Stop without guilt when the timer ends.",
      ];

  return { task: text, category, steps };
}

export const SUGGESTIONS = [
  "Write client proposal",
  "Reply to Sam's email",
  "Fix the login bug",
  "Read the design spec",
  "Submit expense report",
  "Decide on the launch date",
];
